/**
 * Admin Route Guard Component
 * Protects admin routes and ensures only admin users can access them
 */

'use client';

import React from 'react';
import { useAdminAuthGuard } from '@/domains/auth/hooks/use-auth-guard';
import { AuthPendingScreen } from '@/shared/components/feedback/AuthPendingScreen';
import { Alert, Result, Button } from 'antd';
import { LockOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

interface AdminRouteGuardProps {
  /** Component to render if user has admin access */
  children: React.ReactNode;
  /** Whether to require super admin access */
  requireSuperAdmin?: boolean;
  /** Custom loading component */
  loadingComponent?: React.ReactNode;
  /** Custom error component */
  errorComponent?: React.ReactNode;
  /** Custom access denied component */
  accessDeniedComponent?: React.ReactNode;
  /** Redirect path for non-admin users */
  redirectTo?: string;
}

export const AdminRouteGuard: React.FC<AdminRouteGuardProps> = ({
  children,
  requireSuperAdmin = false,
  loadingComponent,
  errorComponent,
  accessDeniedComponent,
  redirectTo = '/auth/login'
}) => {
  const {
    user,
    isAuthenticated,
    hasAccess,
    isAdmin,
    isSuperAdmin,
    isLoading,
    error,
    logout
  } = useAdminAuthGuard(redirectTo);

  // Session/role check — AuthPending only (not app bootstrap)
  if (isLoading) {
    return (
      loadingComponent || (
        <AuthPendingScreen phase="verify" message="Verifying admin access…" />
      )
    );
  }

  // Show error state
  if (error) {
    return errorComponent || (
      <div className="flex items-center justify-center min-h-screen">
        <Result
          status="error"
          icon={<ExclamationCircleOutlined />}
          title="Authentication Error"
          subTitle={error}
          extra={[
            <Button key="retry" type="primary" onClick={() => window.location.reload()}>
              Retry
            </Button>,
            <Button key="logout" onClick={logout}>
              Logout
            </Button>
          ]}
        />
      </div>
    );
  }

  // Show access denied for non-authenticated users
  if (!isAuthenticated && !isLoading) {
    return accessDeniedComponent || (
      <div className="flex items-center justify-center min-h-screen">
        <Result
          status="403"
          icon={<LockOutlined />}
          title="Access Denied"
          subTitle="You must be logged in to access this page."
          extra={[
            <Button key="login" type="primary" href="/auth/login">
              Login
            </Button>
          ]}
        />
      </div>
    );
  }

  // Show access denied for non-admin users
  if (!isAdmin) {
    return accessDeniedComponent || (
      <div className="flex items-center justify-center min-h-screen">
        <Result
          status="403"
          icon={<LockOutlined />}
          title="Admin Access Required"
          subTitle="You must have administrator privileges to access this page."
          extra={[
            <Button key="dashboard" type="primary" href="/dashboard">
              Go to Dashboard
            </Button>,
            <Button key="logout" onClick={logout}>
              Logout
            </Button>
          ]}
        />
      </div>
    );
  }

  // Show access denied for non-super admin users when required
  if (requireSuperAdmin && !isSuperAdmin) {
    return accessDeniedComponent || (
      <div className="flex items-center justify-center min-h-screen">
        <Result
          status="403"
          icon={<LockOutlined />}
          title="Super Admin Access Required"
          subTitle="You must have super administrator privileges to access this page."
          extra={[
            <Button key="admin" type="primary" href="/admin">
              Go to Admin Panel
            </Button>,
            <Button key="logout" onClick={logout}>
              Logout
            </Button>
          ]}
        />
      </div>
    );
  }

  // Show access denied if user doesn't have required access
  if (!hasAccess) {
    return accessDeniedComponent || (
      <div className="flex items-center justify-center min-h-screen">
        <Result
          status="403"
          icon={<LockOutlined />}
          title="Insufficient Permissions"
          subTitle="You don't have the required permissions to access this page."
          extra={[
            <Button key="dashboard" type="primary" href="/dashboard">
              Go to Dashboard
            </Button>,
            <Button key="logout" onClick={logout}>
              Logout
            </Button>
          ]}
        />
      </div>
    );
  }

  // Show admin access info banner
  const showAccessInfo = () => {
    if (!user) return null;

    const roleInfo = requireSuperAdmin && isSuperAdmin
      ? 'Super Administrator'
      : isSuperAdmin
        ? 'Super Administrator'
        : 'Administrator';

    return (
      <Alert
        title={`Admin Access: ${roleInfo}`}
        description={`Logged in as ${user.username} (${user.email})`}
        type="info"
        showIcon
        className="mb-4"
        closable
      />
    );
  };

  // Render protected content
  return (
    <div className="admin-route-guard">
      {showAccessInfo()}
      {children}
    </div>
  );
};

/**
 * Higher-order component for protecting admin routes
 */
export function withAdminGuard<P extends object>(
  Component: React.ComponentType<P>,
  options: Omit<AdminRouteGuardProps, 'children'> = {}
) {
  const WrappedComponent: React.FC<P> = (props) => (
    <AdminRouteGuard {...options}>
      <Component {...props} />
    </AdminRouteGuard>
  );

  WrappedComponent.displayName = `withAdminGuard(${Component.displayName || Component.name})`;
  return WrappedComponent;
}

/**
 * Higher-order component for protecting super admin routes
 */
export function withSuperAdminGuard<P extends object>(
  Component: React.ComponentType<P>,
  options: Omit<AdminRouteGuardProps, 'children' | 'requireSuperAdmin'> = {}
) {
  return withAdminGuard(Component, { ...options, requireSuperAdmin: true });
}
